import { FlexiSetGenericFilterFilterConfig } from './FlexiSetGenericFilterFragment';
import SearchHotelsFilterInput from '@app/model/flexi_sets/input/SearchHotelsFilterInput';
import { FilterChoice } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__flexi-sets/fragment/filter/FilterChoice';
import {
	FlexiSetGenericBooleanFilterFilterConfig,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__flexi-sets/fragment/filter/FlexiSetGenericBooleanFilterFragment';
import {
	FlexiSetGenericRangeFilterFilterConfig,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__flexi-sets/fragment/filter/FlexiSetGenericRangeFilterFragment';
import Range from '@app/model/_common/Range';
import SearchHotelWithinRadiusResult from '@app/model/flexi_sets/SearchHotelWithinRadiusResult';

export const bedroomsFilterConfig: FlexiSetGenericFilterFilterConfig = {
	title: 'Bedrooms',
	choices: [
		{ name: '1 Room', value: 1, mode: 'EQUALS', count: 0 },
		{ name: '2 Rooms', value: 2, mode: 'EQUALS', count: 0 },
		{ name: '3 Rooms', value: 3, mode: 'EQUALS', count: 0 },
		{ name: '4 Rooms', value: 4, mode: 'EQUALS', count: 0 },
		{ name: '5+ Rooms', value: 5, mode: 'MORE_THAN', count: 0 },
	],
	getHotelValue: (hotel: SearchHotelWithinRadiusResult) => hotel.rooms?.[0]?.numberOfBedrooms,
	addToFilter: (filter: SearchHotelsFilterInput, choice: FilterChoice<number>) => {
		if (choice.mode === 'EQUALS') filter.addBedroom(choice.value);
		if (choice.mode === 'MORE_THAN') filter.setMoreThanBedrooms(choice.value);
	},
	removeFromFilter: (filter: SearchHotelsFilterInput, choice: FilterChoice<number>) => {
		if (choice.mode === 'EQUALS') filter.removeBedroom(choice.value);
		if (choice.mode === 'MORE_THAN') filter.removeMoreThanBedrooms();
	},
	isChoiceSelected: (filter: SearchHotelsFilterInput, choice: FilterChoice<number>) => {
		if (choice.mode === 'EQUALS') return filter.bedrooms.includes(choice.value);
		if (choice.mode === 'MORE_THAN') return filter.moreThanBedrooms === choice.value;
		return false;
	},
};

export const bathroomsFilterConfig: FlexiSetGenericFilterFilterConfig = {
	title: 'Bathrooms',
	choices: [
		{ name: '1 Room', value: 1, mode: 'EQUALS', count: 0 },
		{ name: '2 Rooms', value: 2, mode: 'EQUALS', count: 0 },
		{ name: '3 Rooms', value: 3, mode: 'EQUALS', count: 0 },
		{ name: '4 Rooms', value: 4, mode: 'EQUALS', count: 0 },
		{ name: '5+ Rooms', value: 5, mode: 'MORE_THAN', count: 0 },
	],
	getHotelValue: (hotel: SearchHotelWithinRadiusResult) => hotel.rooms?.[0]?.numberOfBathrooms,
	addToFilter: (filter: SearchHotelsFilterInput, choice: FilterChoice<number>) => {
		if (choice.mode === 'EQUALS') filter.addBathroom(choice.value);
		if (choice.mode === 'MORE_THAN') filter.setMoreThanBathrooms(choice.value);
	},
	removeFromFilter: (filter: SearchHotelsFilterInput, choice: FilterChoice<number>) => {
		if (choice.mode === 'EQUALS') filter.removeBathroom(choice.value);
		if (choice.mode === 'MORE_THAN') filter.removeMoreThanBathrooms();
	},
	isChoiceSelected: (filter: SearchHotelsFilterInput, choice: FilterChoice<number>) => {
		if (choice.mode === 'EQUALS') return filter.bathrooms.includes(choice.value);
		if (choice.mode === 'MORE_THAN') return filter.moreThanBathrooms === choice.value;
		return false;
	},
};

export const guestsFilterConfig: FlexiSetGenericFilterFilterConfig = {
	title: 'Guests',
	choices: [
		{ name: '1 Guest', value: 1, mode: 'EQUALS', count: 0 },
		{ name: '2 Guests', value: 2, mode: 'EQUALS', count: 0 },
		{ name: '3 Guests', value: 3, mode: 'EQUALS', count: 0 },
		{ name: '4 Guests', value: 4, mode: 'EQUALS', count: 0 },
		{ name: '5 Guests', value: 5, mode: 'EQUALS', count: 0 },
		{ name: '6+ Guests', value: 6, mode: 'MORE_THAN', count: 0 },
	],
	getHotelValue: (hotel: SearchHotelWithinRadiusResult) => hotel.rooms?.[0]?.maxPersons,
	addToFilter: (filter: SearchHotelsFilterInput, choice: FilterChoice<number>) => {
		if (choice.mode === 'EQUALS') filter.addGuest(choice.value);
		if (choice.mode === 'MORE_THAN') filter.setMoreThanGuests(choice.value);
	},
	removeFromFilter: (filter: SearchHotelsFilterInput, choice: FilterChoice<number>) => {
		if (choice.mode === 'EQUALS') filter.removeGuest(choice.value);
		if (choice.mode === 'MORE_THAN') filter.removeMoreThanGuests();
	},
	isChoiceSelected: (filter: SearchHotelsFilterInput, choice: FilterChoice<number>) => {
		if (choice.mode === 'EQUALS') return filter.guests.includes(choice.value);
		if (choice.mode === 'MORE_THAN') return filter.moreThanGuests === choice.value;
		return false;
	},
};

export const isSuperhostConfig: FlexiSetGenericBooleanFilterFilterConfig = {
	title: 'Superhost',
	choice: { name: 'Is Superhost', value: true, mode: 'EQUALS', count: 0 },
	getHotelValue: (hotel: SearchHotelWithinRadiusResult) => hotel.isSuperhost,
	addToFilter: (filter: SearchHotelsFilterInput, choice: FilterChoice<boolean>) => {
		filter.setIsSuperhost(true);
	},
	removeFromFilter: (filter: SearchHotelsFilterInput, choice: FilterChoice<boolean>) => {
		filter.removeIsSuperhost();
	},
	isChoiceSelected: (filter: SearchHotelsFilterInput, choice: FilterChoice<boolean>) => {
		return !!filter.isSuperhost && filter.isSuperhost;
	},
};

export const ratingRangeConfig: FlexiSetGenericRangeFilterFilterConfig = {
	title: 'Rating',
	getHotelValue: (hotel: SearchHotelWithinRadiusResult) => hotel.reviewScore?.average || 0,
	increment: 0.5,
	setToFilter: (filter: SearchHotelsFilterInput, range: Range) => {
		filter.setRating(range.from, range.to);
	},
};

export const reviewsCountRangeConfig: FlexiSetGenericRangeFilterFilterConfig = {
	title: 'Number of Reviews',
	getHotelValue: (hotel: SearchHotelWithinRadiusResult) => hotel.reviewScore?.sum || 0,
	increment: 10,
	setToFilter: (filter: SearchHotelsFilterInput, range: Range) => {
		filter.setReviewsCount(range.from, range.to);
	},
};

export const averageNightRangeRangeConfig: FlexiSetGenericRangeFilterFilterConfig = {
	title: 'Average Night Rate',
	getHotelValue: (hotel: SearchHotelWithinRadiusResult) => hotel.statistics?.average_night_rate_365 || 0,
	increment: 10,
	setToFilter: (filter: SearchHotelsFilterInput, range: Range) => {
		filter.setMedianPrice(range.from, range.to);
	},
};
